import React, { useState, useEffect } from 'react';
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Tabs, Tab, Stack, Badge, Alert, TablePagination,
} from '@mui/material';
import { attendanceService, leaveRequestService, holidayService, attendanceRegularizationService } from '../services/api';
import { format, parseISO, isWithinInterval, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { usePagination } from '../hooks/usePagination';
import { ATTENDANCE_COLORS } from '../constants/attendanceColors';
import { AttendanceDayGrid, buildAttendanceDayRows } from './shared/AttendanceDayGrid';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

// One employee's leave requests — same reasoning as the shared AttendanceDayGrid.
const EmployeeLeaveTable: React.FC<{
  leaves: any[];
  getStatusColor: (s: string) => any;
  onApprove: (id: number) => void;
  onReject: (leave: any) => void;
  onRevoke: (id: number) => void;
}> = ({ leaves, getStatusColor, onApprove, onReject, onRevoke }) => {
  const { pagedRows, paginationProps } = usePagination(leaves);
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Start Date</TableCell>
            <TableCell>End Date</TableCell>
            <TableCell>Leave Type</TableCell>
            <TableCell>Leave Detail (Days)</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRows.map((leave) => {
            // "Leave detail" split — Total requested / Paid Leave / Leave Without Pay / balance
            // after approval. BalanceAfterApproval/CurrentBalance only come back from the API
            // for still-Pending, balance-tracked requests (see LeaveRequestsController.GetAllLeaveRequests).
            const isPartialSplit = leave.unpaidDays > 0 && leave.paidDays > 0;
            const isFullyUnpaid = leave.unpaidDays > 0 && leave.paidDays <= 0;
            return (
            <TableRow key={leave.id}>
              <TableCell>{format(new Date(leave.startDate), 'MMM dd, yyyy')}</TableCell>
              <TableCell>{format(new Date(leave.endDate), 'MMM dd, yyyy')}</TableCell>
              <TableCell>
                {leave.leaveType}
                {leave.dayType === 'Half' && (
                  <Chip label="Half Day" size="small" variant="outlined" sx={{ ml: 1 }} />
                )}
                {isFullyUnpaid && leave.leaveType !== 'Leave Without Pay' && (
                  <Chip label="Unpaid" color="warning" size="small" sx={{ ml: 1 }} />
                )}
              </TableCell>
              <TableCell>
                {leave.totalDays != null ? (
                  <Box sx={{ fontSize: '0.8rem' }}>
                    <div>Total: {leave.totalDays}</div>
                    {isPartialSplit ? (
                      <>
                        <div>Paid: {leave.paidDays}</div>
                        <div>LWP: {leave.unpaidDays}</div>
                      </>
                    ) : null}
                    {leave.status === 'Pending' && leave.balanceAfterApproval != null && (
                      <div>Balance after: {leave.balanceAfterApproval}</div>
                    )}
                  </Box>
                ) : '-'}
              </TableCell>
              <TableCell>{leave.reason}</TableCell>
              <TableCell>
                <Chip label={leave.status} color={getStatusColor(leave.status)} size="small" />
              </TableCell>
              <TableCell>
                {leave.status === 'Pending' && (
                  <>
                    <Button size="small" color="success" onClick={() => onApprove(leave.id)} sx={{ mr: 1 }}>
                      Approve
                    </Button>
                    <Button size="small" color="error" onClick={() => onReject(leave)}>
                      Reject
                    </Button>
                  </>
                )}
                {leave.status === 'Approved' && (
                  <Button size="small" color="warning" onClick={() => onRevoke(leave.id)}>
                    Revoke
                  </Button>
                )}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {leaves.length > 0 && <TablePagination {...paginationProps} />}
    </TableContainer>
  );
};

const AdminAttendanceLeave: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [attendance, setAttendance] = useState<any[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [holidays, setHolidays] = useState<any[]>([]);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [message, setMessage] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<'all' | 'date' | 'month' | 'week'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedWeekStart, setSelectedWeekStart] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  // Regularization state
  const [regularizations, setRegularizations] = useState<any[]>([]);
  const [regRejectDialog, setRegRejectDialog] = useState(false);
  const [selectedReg, setSelectedReg] = useState<any>(null);
  const [regRejectionReason, setRegRejectionReason] = useState('');
  const [regEmployeeFilter, setRegEmployeeFilter] = useState('');
  const [rejectAttendanceDialog, setRejectAttendanceDialog] = useState(false);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState<any>(null);
  const [regStatusFilter, setRegStatusFilter] = useState('');

  const pendingRegCount = regularizations.filter(r => r.status === 'Pending').length;

  const regEmployeeOptions = Array.from(
    new Set(regularizations.map(r => r.employeeName || r.employeeUsername).filter(Boolean))
  ).sort();

  const filteredRegularizations = regularizations.filter(r => {
    const nameMatch = !regEmployeeFilter || (r.employeeName || r.employeeUsername) === regEmployeeFilter;
    const statusMatch = !regStatusFilter || r.status === regStatusFilter;
    return nameMatch && statusMatch;
  });

  useEffect(() => {
    loadAttendance();
    loadLeaveRequests();
    loadHolidays();
    loadRegularizations();
  }, []);

  const loadHolidays = async () => {
    try {
      const data = await holidayService.getHolidays();
      setHolidays(data);
    } catch (error) {
      console.error('Error loading holidays:', error);
    }
  };

  const loadAttendance = async () => {
    try {
      const data = await attendanceService.getAllAttendance();
      setAttendance(data);
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const loadLeaveRequests = async () => {
    try {
      const data = await leaveRequestService.getAllLeaveRequests();
      setLeaveRequests(data);
    } catch (error) {
      console.error('Error loading leave requests:', error);
    }
  };

  const loadRegularizations = async () => {
    try {
      const data = await attendanceRegularizationService.getAll();
      setRegularizations(data);
    } catch (error) {
      console.error('Error loading regularizations:', error);
    }
  };

  const handleApproveReg = async (id: number) => {
    try {
      await attendanceRegularizationService.approve(id);
      setMessage('Regularization approved — attendance record updated.');
      loadRegularizations();
      loadAttendance();
      setTimeout(() => setMessage(''), 4000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error approving regularization.');
    }
  };

  const handleRejectAttendanceClick = (record: any) => {
    setSelectedAttendanceRecord(record);
    setRejectAttendanceDialog(true);
  };

  const handleConfirmRejectAttendance = async () => {
    if (!selectedAttendanceRecord) return;
    try {
      await attendanceService.deleteAttendance(selectedAttendanceRecord.id);
      setMessage('Attendance record removed.');
      setRejectAttendanceDialog(false);
      loadAttendance();
      setTimeout(() => setMessage(''), 4000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error removing attendance record.');
    }
  };

  const handleRejectRegClick = (reg: any) => {
    setSelectedReg(reg);
    setRegRejectionReason('');
    setRegRejectDialog(true);
  };

  const handleRejectReg = async () => {
    if (!selectedReg || !regRejectionReason.trim()) return;
    try {
      await attendanceRegularizationService.reject(selectedReg.id, regRejectionReason);
      setMessage('Regularization request rejected.');
      setRegRejectDialog(false);
      loadRegularizations();
      setTimeout(() => setMessage(''), 4000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error rejecting regularization.');
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await leaveRequestService.approveLeaveRequest(id);
      setMessage('Leave request approved');
      loadLeaveRequests();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error approving leave');
    }
  };

  const handleRejectClick = (leave: any) => {
    setSelectedLeave(leave);
    setRejectDialog(true);
  };

  const handleReject = async () => {
    if (!selectedLeave || !rejectionReason) return;
    try {
      await leaveRequestService.rejectLeaveRequest(selectedLeave.id, rejectionReason);
      setMessage('Leave request rejected');
      setRejectDialog(false);
      setRejectionReason('');
      loadLeaveRequests();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error rejecting leave');
    }
  };

  const handleRevoke = async (id: number) => {
    if (window.confirm('Are you sure you want to revoke this approved leave? Balance will be restored.')) {
      try {
        await leaveRequestService.revokeLeaveRequest(id);
        setMessage('Leave revoked and balance restored');
        loadLeaveRequests();
        setTimeout(() => setMessage(''), 3000);
      } catch (error: any) {
        setMessage(error.response?.data?.message || 'Error revoking leave');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      case 'Present': return 'success';
      case 'Cancelled': return 'default';
      default: return 'warning';
    }
  };

  const toggleEmployee = (employeeName: string) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(employeeName)) {
      newExpanded.delete(employeeName);
    } else {
      newExpanded.add(employeeName);
    }
    setExpandedEmployees(newExpanded);
  };

  const groupByEmployee = (records: any[]) => {
    const grouped: { [key: string]: any[] } = {};
    records.forEach(record => {
      const name = record.userName;
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(record);
    });
    // Sort each employee's records newest first
    Object.keys(grouped).forEach(name => {
      grouped[name].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });
    return grouped;
  };

  const filterByDate = (records: any[], dateField: string) => {
    if (filterType === 'all') return records;

    return records.filter((record) => {
      const date = parseISO(record[dateField]);

      switch (filterType) {
        case 'date':
          if (startDate && endDate) {
            const start = parseISO(startDate);
            const end = parseISO(endDate);
            return isWithinInterval(date, { start, end });
          }
          return true;

        case 'month':
          const monthStart = startOfMonth(parseISO(selectedMonth + '-01'));
          const monthEnd = endOfMonth(monthStart);
          return isWithinInterval(date, { start: monthStart, end: monthEnd });

        case 'week':
          const weekStart = startOfWeek(parseISO(selectedWeekStart), { weekStartsOn: 1 });
          const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
          return isWithinInterval(date, { start: weekStart, end: weekEnd });

        default:
          return true;
      }
    });
  };

  const getCalendarDays = () => {
    const monthStart = startOfMonth(parseISO(selectedMonth + '-01'));
    const monthEnd = endOfMonth(monthStart);
    const days = [];
    
    // Add empty cells for days before month starts (Monday = 1)
    const startDay = monthStart.getDay();
    const emptyDays = startDay === 0 ? 6 : startDay - 1; // Sunday = 6 empty days
    for (let i = 0; i < emptyDays; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  const getAttendanceStatus = (employeeName: string, date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayOfWeek = date.getDay();
    
    // Check if weekend (Saturday=6, Sunday=0) — but show P if attendance was actually marked
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      const weekendRecord = filteredAttendance.find(
        r => r.userName === employeeName && format(parseISO(r.date), 'yyyy-MM-dd') === dateStr
      );
      return weekendRecord ? (weekendRecord.status === 'Present' ? 'P' : weekendRecord.status.charAt(0)) : '-';
    }
    
    // Check if holiday
    const isHoliday = holidays.some(h => format(parseISO(h.date), 'yyyy-MM-dd') === dateStr);
    if (isHoliday) {
      return 'H';
    }
    
    // Check if approved leave
    const leave = leaveRequests.find(l => {
      if (l.userName !== employeeName || l.status !== 'Approved') return false;
      const leaveStart = parseISO(l.startDate);
      const leaveEnd = parseISO(l.endDate);
      return isWithinInterval(date, { start: leaveStart, end: leaveEnd });
    });
    
    if (leave) {
      // Half day shows with a "½" suffix (e.g. "S½") so it's visually distinct from a full
      // day of the same leave type on the calendar.
      const half = leave.dayType === 'Half' ? '½' : '';
      // Unpaid takes priority over the originally-selected type — covers both an explicit
      // "Leave Without Pay" pick and a Sick/Earned Leave request auto-flagged unpaid for
      // lack of balance at submission time.
      if (leave.isUnpaid || leave.leaveType === 'Leave Without Pay' || leave.leaveType === 'leave without pay') return 'LWP' + half;
      if (leave.leaveType === 'Sick Leave' || leave.leaveType === 'sick leave') return 'S' + half;
      if (leave.leaveType === 'Earned Leave' || leave.leaveType === 'earn leave') return 'E' + half;
      if (leave.leaveType === 'Transfer Leave') return 'T' + half;
      if (leave.leaveType === 'Paternity Leave') return 'P' + half;
      if (leave.leaveType === 'Bereavement Leave') return 'B' + half;
      if (leave.leaveType === 'Comp Off Leave') return 'C' + half;
      return 'L' + half;
    }
    
    const record = filteredAttendance.find(
      r => r.userName === employeeName && format(parseISO(r.date), 'yyyy-MM-dd') === dateStr
    );
    return record ? (record.status === 'Present' ? 'P' : record.status.charAt(0)) : 'A';
  };

  const getCalendarStatusColor = (status: string) => {
    if (status === 'P') return ATTENDANCE_COLORS.present.dot;
    if (status === 'A') return ATTENDANCE_COLORS.missing.dot;
    if (status === 'H') return ATTENDANCE_COLORS.holiday.dot;
    if (status === '-') return ATTENDANCE_COLORS.weekend.dot;
    return ATTENDANCE_COLORS.leave.dot; // S / E / LWP / L
  };

  const filteredAttendance = filterByDate(attendance, 'date');
  const filteredLeaveRequests = filterByDate(leaveRequests, 'startDate');
  const { pagedRows: pagedRegularizations, paginationProps: regularizationsPaginationProps } = usePagination(filteredRegularizations);

  // The bounded period the per-employee attendance grid enumerates every date for. "All" has
  // no natural bound, so it falls back to the current month — a full, scannable grid needs a
  // finite range, and a month is the natural unit HR reviews attendance by.
  const getEffectiveDateRange = (): { start: Date; end: Date } => {
    if (filterType === 'month') {
      const start = startOfMonth(parseISO(selectedMonth + '-01'));
      return { start, end: endOfMonth(start) };
    }
    if (filterType === 'week') {
      const start = startOfWeek(parseISO(selectedWeekStart), { weekStartsOn: 1 });
      return { start, end: endOfWeek(start, { weekStartsOn: 1 }) };
    }
    if (filterType === 'date' && startDate && endDate) {
      return { start: parseISO(startDate), end: parseISO(endDate) };
    }
    const start = startOfMonth(new Date());
    return { start, end: endOfMonth(start) };
  };

  // One employee's day-by-day rows for the shared AttendanceDayGrid — see buildAttendanceDayRows
  // for how records/leave/holidays are merged into a row per date.
  const buildDayRows = (employeeName: string, employeeRecords: any[]) => {
    const approvedLeaves = leaveRequests.filter(l => l.userName === employeeName && l.status === 'Approved');
    return buildAttendanceDayRows(employeeRecords, approvedLeaves, holidays, getEffectiveDateRange());
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Attendance & Leave Management</Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Filter by Date</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button 
            variant={filterType === 'all' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => setFilterType('all')}
          >
            All
          </Button>
          <Button 
            variant={filterType === 'date' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => setFilterType('date')}
          >
            Date Range
          </Button>
          <Button 
            variant={filterType === 'month' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => setFilterType('month')}
          >
            Month
          </Button>
          <Button 
            variant={filterType === 'week' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => setFilterType('week')}
          >
            Week
          </Button>
        </Box>

        {filterType === 'date' && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Box>
        )}

        {filterType === 'month' && (
          <TextField
            label="Select Month"
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        )}

        {filterType === 'week' && (
          <TextField
            label="Week Start (Monday)"
            type="date"
            value={selectedWeekStart}
            onChange={(e) => setSelectedWeekStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        )}
      </Paper>

      <Paper sx={{ width: '100%', borderRadius: '10px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label="Attendance" />
            <Tab label="Leave Requests" />
            <Tab label={
              <Badge badgeContent={pendingRegCount} color="error" invisible={pendingRegCount === 0}>
                Regularization
              </Badge>
            } />
          </Tabs>
          {currentTab === 0 && (
            <Box sx={{ mr: 2 }}>
              <Button 
                size="small" 
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('list')}
                sx={{ mr: 1 }}
              >
                List
              </Button>
              <Button 
                size="small" 
                variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('calendar')}
              >
                Calendar
              </Button>
            </Box>
          )}
        </Box>

        <TabPanel value={currentTab} index={0}>
          {viewMode === 'calendar' ? (
            <Box>
              <TextField
                label="Select Month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ mb: 2 }}
              />
              {Object.keys(groupByEmployee(attendance)).map((employeeName) => (
                <Box key={employeeName} sx={{ mb: 3 }}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      mb: 1,
                      cursor: 'pointer',
                      bgcolor: 'primary.light',
                      '&:hover': { bgcolor: 'primary.main' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      borderRadius: '10px',
                    }}
                    onClick={() => toggleEmployee(employeeName)}
                  >
                    <Typography sx={{ fontSize: 14, color: 'white' }}>
                      {expandedEmployees.has(employeeName) ? '▼' : '▶'}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white', flex: 1 }}>
                      {employeeName}
                    </Typography>
                  </Paper>
                  {expandedEmployees.has(employeeName) && (
                    <Paper sx={{ p: 2 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <Box key={day} sx={{ textAlign: 'center', fontWeight: 'bold', p: 1 }}>
                            {day}
                          </Box>
                        ))}
                        {getCalendarDays().map((date, idx) => {
                          if (!date) {
                            return <Box key={idx} />;
                          }
                          const status = getAttendanceStatus(employeeName, date);
                          return (
                            <Box 
                              key={idx}
                              sx={{ 
                                textAlign: 'center', 
                                p: 1, 
                                border: 1, 
                                borderColor: 'divider',
                                bgcolor: getCalendarStatusColor(status) + '20',
                                borderRadius: 1
                              }}
                            >
                              <Typography variant="caption" display="block">{format(date, 'd')}</Typography>
                              <Typography variant="h6" sx={{ color: getCalendarStatusColor(status), fontWeight: 'bold' }}>
                                {status}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Chip label="P - Present" size="small" sx={{ bgcolor: '#4caf5020' }} />
                        <Chip label="A - Absent" size="small" sx={{ bgcolor: '#f4433620' }} />
                        <Chip label="H - Holiday" size="small" sx={{ bgcolor: '#2196f320' }} />
                        <Chip label="- - Weekend" size="small" sx={{ bgcolor: '#9e9e9e20' }} />
                        <Chip label="S - Sick Leave" size="small" sx={{ bgcolor: '#ff980020' }} />
                        <Chip label="E - Earned Leave" size="small" sx={{ bgcolor: '#ff980020' }} />
                        <Chip label="LWP - Unpaid" size="small" sx={{ bgcolor: '#ff980020' }} />
                      </Box>
                    </Paper>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            Object.entries(groupByEmployee(filteredAttendance)).map(([employeeName, records]) => (
              <Box key={employeeName} sx={{ mb: 2 }}>
                <Paper
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: 'pointer',
                    bgcolor: 'primary.light',
                    '&:hover': { bgcolor: 'primary.main' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: '10px',
                  }}
                  onClick={() => toggleEmployee(employeeName)}
                >
                  <Typography sx={{ fontSize: 14, color: 'white' }}>
                    {expandedEmployees.has(employeeName) ? '▼' : '▶'}
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'white', flex: 1 }}>
                    {employeeName}
                  </Typography>
                  <Chip
                    label={`${records.length} records`}
                    size="small"
                    sx={{ bgcolor: 'white', fontWeight: 600 }}
                  />
                </Paper>
                {expandedEmployees.has(employeeName) && (
                  <AttendanceDayGrid
                    rows={buildDayRows(employeeName, records)}
                    renderAction={(row) => row.record && (
                      <Button size="small" variant="outlined" color="error" onClick={() => handleRejectAttendanceClick(row.record)}>
                        Reject
                      </Button>
                    )}
                  />
                )}
              </Box>
            ))
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          {Object.entries(groupByEmployee(filteredLeaveRequests)).map(([employeeName, leaves]) => (
            <Box key={employeeName} sx={{ mb: 2 }}>
              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 1,
                  cursor: 'pointer',
                  bgcolor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.main' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: '10px',
                }}
                onClick={() => toggleEmployee(employeeName)}
              >
                <Typography sx={{ fontSize: 14, color: 'white' }}>
                  {expandedEmployees.has(employeeName) ? '▼' : '▶'}
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', flex: 1 }}>
                  {employeeName}
                </Typography>
                <Chip 
                  label={`${leaves.length} requests`} 
                  size="small" 
                  sx={{ bgcolor: 'white', fontWeight: 600 }}
                />
              </Paper>
              {expandedEmployees.has(employeeName) && (
                <EmployeeLeaveTable
                  leaves={leaves}
                  getStatusColor={getStatusColor}
                  onApprove={handleApprove}
                  onReject={handleRejectClick}
                  onRevoke={handleRevoke}
                />
              )}
            </Box>
          ))}
        </TabPanel>

        {/* ── Regularization Requests Tab ─────────────────────────────── */}
        <TabPanel value={currentTab} index={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              All Regularization Requests
              {pendingRegCount > 0 && (
                <Chip label={`${pendingRegCount} Pending`} color="error" size="small" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                select size="small" label="Employee" value={regEmployeeFilter}
                onChange={e => setRegEmployeeFilter(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All Employees</MenuItem>
                {regEmployeeOptions.map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Status" value={regStatusFilter}
                onChange={e => setRegStatusFilter(e.target.value)}
                sx={{ minWidth: 130 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </TextField>
            </Stack>
          </Stack>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Employee', 'Date', 'Check In', 'Check Out', 'Status Req.', 'Reason', 'Submitted At', 'Status', 'Actions'].map(h => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRegularizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No regularization requests found.
                    </TableCell>
                  </TableRow>
                ) : pagedRegularizations.map((reg) => (
                  <TableRow key={reg.id} hover sx={{ bgcolor: reg.status === 'Pending' ? 'warning.50' : 'inherit' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{reg.employeeName || reg.employeeUsername}</Typography>
                    </TableCell>
                    <TableCell>{format(new Date(reg.requestedDate), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>{reg.requestedCheckIn}</TableCell>
                    <TableCell>{reg.requestedCheckOut || '—'}</TableCell>
                    <TableCell>{reg.requestedStatus}</TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }}>{reg.reason}</TableCell>
                    <TableCell>{format(new Date(reg.requestedAt), 'MMM dd, yyyy HH:mm')}</TableCell>
                    <TableCell>
                      <Chip
                        label={reg.status}
                        size="small"
                        color={reg.status === 'Approved' ? 'success' : reg.status === 'Rejected' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {reg.status === 'Pending' && (
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" color="success" onClick={() => handleApproveReg(reg.id)}>
                            Approve
                          </Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => handleRejectRegClick(reg)}>
                            Reject
                          </Button>
                        </Stack>
                      )}
                      {reg.status === 'Rejected' && (
                        <Typography variant="caption" color="error">{reg.rejectionReason}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredRegularizations.length > 0 && <TablePagination {...regularizationsPaginationProps} />}
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Attendance record reject confirmation dialog */}
      <Dialog open={rejectAttendanceDialog} onClose={() => setRejectAttendanceDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Attendance Record?</DialogTitle>
        <DialogContent>
          {selectedAttendanceRecord && (
            <Typography variant="body2">
              This will permanently remove the attendance record for <strong>{selectedAttendanceRecord.userName}</strong> on <strong>{format(new Date(selectedAttendanceRecord.date), 'MMM dd, yyyy')}</strong>. This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectAttendanceDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmRejectAttendance}>
            Confirm Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regularization reject dialog */}
      <Dialog open={regRejectDialog} onClose={() => setRegRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Regularization Request</DialogTitle>
        <DialogContent>
          {selectedReg && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selectedReg.employeeName} — {format(new Date(selectedReg.requestedDate), 'MMM dd, yyyy')}
            </Typography>
          )}
          <TextField
            fullWidth label="Rejection Reason" value={regRejectionReason}
            onChange={e => setRegRejectionReason(e.target.value)}
            margin="normal" multiline rows={3} required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleRejectReg} variant="contained" color="error" disabled={!regRejectionReason.trim()}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error">Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAttendanceLeave;
